import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  MenuItem,
  Pagination,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PrintIcon from '@mui/icons-material/Print';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import TransformIcon from '@mui/icons-material/Transform';
import {
  cancelPedidoComercial,
  converterPedidoComercial,
  faturarPedidoComercial,
  getPedidoComercial,
  getPedidoComercialImpressao,
  getPedidoComercialNfePayload,
  getPedidosComerciais,
} from '../services/api';
import { getAuthPermissoes } from '../services/authStorage';
import { getApiErrorMessage } from '../utils/apiError';
import PageHeader from './common/PageHeader';
import ResponsiveTable from './common/ResponsiveTable';
import { useEstabelecimentoFiltroApi } from '../context/EstabelecimentoContext';
import { abrirImpressaoPedidoComercial } from '../utils/pedidoComercialPrint';

const STATUS_COLOR = {
  Aberto: 'success',
  EmCaixa: 'warning',
  Finalizado: 'default',
  Cancelado: 'error',
  Convertido: 'info',
};

const money = (value) =>
  Number(value || 0).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  });

const fmtDate = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('pt-BR');
};

/** @param {{ tipoFixo?: 'Orcamento' | 'Pedido' }} props */
export default function PedidoComercialList({ tipoFixo }) {
  const navigate = useNavigate();
  const estabelecimentoId = useEstabelecimentoFiltroApi();
  const permissions = useMemo(() => getAuthPermissoes(), []);
  const can = useCallback(
    (key) => Boolean(permissions?.[key]),
    [permissions],
  );

  const isPedido = tipoFixo === 'Pedido';
  const basePath = isPedido ? '/pedidos-venda' : '/orcamentos';
  const titulo = isPedido ? 'Pedidos de venda' : 'Orçamentos';
  const subtitulo = isPedido
    ? 'Pedidos (PED######) bipáveis no PDV. Fature ou emita NF-e.'
    : 'Orçamentos (ORC######) bipáveis no PDV. Converta em pedido de venda quando fechar.';

  const emptyFilters = useCallback(
    () => ({
      status: 'Aberto',
      tipo: tipoFixo || '',
      termo: '',
    }),
    [tipoFixo],
  );

  const [filters, setFilters] = useState(emptyFilters);
  const [applied, setApplied] = useState(emptyFilters);
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ items: [], totalPages: 1, totalItems: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [detail, setDetail] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const next = emptyFilters();
    setFilters(next);
    setApplied(next);
    setPage(1);
  }, [emptyFilters]);

  const load = useCallback(async () => {
    if (!can('orcamento')) {
      setError('Sem permissão de orçamento/pedido.');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const response = await getPedidosComerciais({
        ...applied,
        tipo: tipoFixo || applied.tipo || undefined,
        estabelecimentoId: estabelecimentoId || undefined,
        indice: page,
        qtde: 20,
      });
      const payload = response.data || {};
      const totalItems = payload.totalItems || 0;
      const pageSize = payload.pageSize || 20;
      setData({
        items: payload.items || [],
        totalItems,
        totalPages: Math.max(1, Math.ceil(totalItems / pageSize)),
      });
    } catch (e) {
      setError(getApiErrorMessage(e, 'Não foi possível carregar os documentos.'));
    } finally {
      setLoading(false);
    }
  }, [applied, page, can, estabelecimentoId, tipoFixo]);

  useEffect(() => {
    load();
  }, [load]);

  const openDetail = async (rowId) => {
    setDetailOpen(true);
    setBusy(true);
    try {
      const { data: d } = await getPedidoComercial(rowId);
      setDetail(d);
    } catch (e) {
      setError(getApiErrorMessage(e, 'Erro ao carregar detalhe.'));
      setDetailOpen(false);
    } finally {
      setBusy(false);
    }
  };

  const handleCancel = async () => {
    if (!detail?.id) return;
    if (!window.confirm(`Cancelar ${detail.codigo}?`)) return;
    setBusy(true);
    try {
      await cancelPedidoComercial(detail.id, { motivo: 'Cancelado no Gestão' });
      setDetailOpen(false);
      await load();
    } catch (e) {
      setError(getApiErrorMessage(e, 'Não foi possível cancelar.'));
    } finally {
      setBusy(false);
    }
  };

  const handleConverter = async () => {
    if (!detail?.id) return;
    if (!window.confirm(`Converter ${detail.codigo} em pedido de venda?`)) return;
    setBusy(true);
    try {
      const { data: ped } = await converterPedidoComercial(detail.id);
      setDetailOpen(false);
      await load();
      navigate(`/pedidos-venda/${ped.id}`);
    } catch (e) {
      setError(getApiErrorMessage(e, 'Falha ao converter.'));
    } finally {
      setBusy(false);
    }
  };

  const handlePrint = async (modo) => {
    const id = detail?.id;
    if (!id) return;
    setBusy(true);
    try {
      const { data: imp } = await getPedidoComercialImpressao(id);
      await abrirImpressaoPedidoComercial(imp, { modo });
    } catch (e) {
      setError(getApiErrorMessage(e, 'Falha na impressão.'));
    } finally {
      setBusy(false);
    }
  };

  const handleNfe = async () => {
    if (!detail?.id) return;
    setBusy(true);
    try {
      const { data: payload } = await getPedidoComercialNfePayload(detail.id);
      sessionStorage.setItem(
        'zen.nfe.prefill.pedidoComercial',
        JSON.stringify(payload),
      );
      navigate('/fiscal/nfes');
    } catch (e) {
      setError(getApiErrorMessage(e, 'Não foi possível preparar NF-e.'));
    } finally {
      setBusy(false);
    }
  };

  const handleFaturar = async () => {
    if (!detail?.id) return;
    if (!detail.formaPagamentoId) {
      setError('Salve uma forma de pagamento no pedido antes de faturar.');
      return;
    }
    if (!window.confirm(`Faturar ${detail.codigo} sem emissão fiscal?`)) return;
    setBusy(true);
    try {
      const { data: d } = await faturarPedidoComercial(detail.id, {
        formaPagamentoId: detail.formaPagamentoId,
        semFiscal: true,
      });
      setDetail(d);
      await load();
    } catch (e) {
      setError(getApiErrorMessage(e, 'Não foi possível faturar.'));
    } finally {
      setBusy(false);
    }
  };

  const clienteLinha = (row) => {
    const nome = row.clienteNome || 'Consumidor';
    const extra = [
      row.clienteDocumento || row.documento,
      row.clienteTelefone,
      row.clienteCidade,
    ].filter(Boolean);
    return (
      <Box>
        <Typography variant="body2" fontWeight={700}>{nome}</Typography>
        {extra.length > 0 && (
          <Typography variant="caption" color="text.secondary" display="block">
            {extra.join(' · ')}
          </Typography>
        )}
      </Box>
    );
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <PageHeader
        title={titulo}
        subtitle={subtitulo}
        actions={
          <Button variant="contained" onClick={() => navigate(`${basePath}/novo`)}>
            {isPedido ? 'Novo pedido de venda' : 'Novo orçamento'}
          </Button>
        }
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} flexWrap="wrap" useFlexGap>
          <TextField
            select
            label="Status"
            size="small"
            value={filters.status}
            onChange={(e) => setFilters((p) => ({ ...p, status: e.target.value }))}
            sx={{ minWidth: 150 }}
          >
            <MenuItem value="">Todos</MenuItem>
            <MenuItem value="Aberto">Abertos</MenuItem>
            <MenuItem value="EmCaixa">No caixa</MenuItem>
            <MenuItem value="Finalizado">Finalizados</MenuItem>
            <MenuItem value="Cancelado">Cancelados</MenuItem>
            {!isPedido && <MenuItem value="Convertido">Convertidos</MenuItem>}
          </TextField>
          <TextField
            label="Busca"
            size="small"
            placeholder={isPedido ? 'PED000123, cliente…' : 'ORC000123, cliente…'}
            value={filters.termo}
            onChange={(e) => setFilters((p) => ({ ...p, termo: e.target.value }))}
            sx={{ minWidth: 220 }}
          />
          <Button
            variant="contained"
            disabled={loading}
            onClick={() => {
              setPage(1);
              setApplied({ ...filters, tipo: tipoFixo || filters.tipo });
            }}
          >
            Filtrar
          </Button>
        </Stack>
      </Paper>

      {loading && <LinearProgress />}
      <ResponsiveTable>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Código</TableCell>
              <TableCell>Cliente</TableCell>
              <TableCell>Pagamento</TableCell>
              <TableCell>Criação</TableCell>
              <TableCell align="right">Total</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(data.items || []).map((row) => (
              <TableRow key={row.id} hover>
                <TableCell>
                  <Typography fontWeight={800}>{row.codigo}</Typography>
                </TableCell>
                <TableCell>{clienteLinha(row)}</TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {row.formaPagamentoNome || '—'}
                  </Typography>
                </TableCell>
                <TableCell>{fmtDate(row.dataCriacao)}</TableCell>
                <TableCell align="right">{money(row.valorTotal)}</TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={row.status}
                    color={STATUS_COLOR[row.status] || 'default'}
                  />
                </TableCell>
                <TableCell align="right" data-actions="" onClick={(e) => e.stopPropagation()}>
                  <Tooltip title="Detalhe">
                    <IconButton color="primary" onClick={() => openDetail(row.id)}>
                      <VisibilityIcon />
                    </IconButton>
                  </Tooltip>
                  {row.status === 'Aberto' && (
                    <Tooltip title="Editar">
                      <IconButton
                        color="primary"
                        onClick={() => navigate(`${basePath}/${row.id}/editar`)}
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                  <Tooltip title="Imprimir A4">
                    <IconButton
                      color="secondary"
                      onClick={async () => {
                        setBusy(true);
                        try {
                          const { data: imp } = await getPedidoComercialImpressao(row.id);
                          await abrirImpressaoPedidoComercial(imp, { modo: 'a4' });
                        } catch (e) {
                          setError(getApiErrorMessage(e, 'Falha na impressão.'));
                        } finally {
                          setBusy(false);
                        }
                      }}
                    >
                      <PrintIcon />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
            {!loading && (data.items || []).length === 0 && (
              <TableRow>
                <TableCell colSpan={7}>
                  <Typography color="text.secondary" align="center" sx={{ py: 3 }}>
                    Nenhum documento encontrado.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </ResponsiveTable>

      {(data.totalPages || 1) > 1 && (
        <Stack alignItems="center" sx={{ mt: 2 }}>
          <Pagination
            page={page}
            count={data.totalPages || 1}
            onChange={(_, value) => setPage(value)}
          />
        </Stack>
      )}

      <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {detail?.codigo || 'Detalhe'} · {detail?.status}
        </DialogTitle>
        <DialogContent dividers>
          {busy && !detail ? (
            <LinearProgress />
          ) : detail ? (
            <Stack spacing={1.25}>
              <Paper variant="outlined" sx={{ p: 1.5 }}>
                <Typography variant="caption" color="text.secondary">Cliente</Typography>
                <Typography fontWeight={800}>{detail.clienteNome || 'Consumidor'}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {[
                    detail.clienteDocumento || detail.documento,
                    detail.clienteTelefone,
                    detail.clienteEmail,
                    detail.clienteEndereco,
                    detail.clienteCidade,
                  ].filter(Boolean).join(' · ') || 'Sem dados cadastrais'}
                </Typography>
              </Paper>
              <Typography>
                Pagamento: <strong>{detail.formaPagamentoNome || '—'}</strong>
              </Typography>
              <Typography>Total: <strong>{money(detail.valorTotal)}</strong></Typography>
              {detail.vendaId ? (
                <Typography variant="body2" color="success.main">
                  Venda gerada #{detail.vendaId}
                </Typography>
              ) : null}
              {(detail.itens || []).map((i) => (
                <Typography key={i.id} variant="body2">
                  {i.produtoCodigo} · {i.produtoDescricao} — {i.quantidade} x {money(i.valorUnitario)}
                  {Number(i.desconto) > 0 ? ` (−${money(i.desconto)})` : ''}
                  {' = '}{money(i.valorTotal)}
                </Typography>
              ))}
            </Stack>
          ) : null}
        </DialogContent>
        <DialogActions sx={{ flexWrap: 'wrap', gap: 0.5 }}>
          <Button startIcon={<PrintIcon />} onClick={() => handlePrint('80mm')} disabled={busy}>
            80mm
          </Button>
          <Button startIcon={<PrintIcon />} onClick={() => handlePrint('a4')} disabled={busy}>
            A4
          </Button>
          {detail?.tipo === 'Orcamento' && detail?.status === 'Aberto' && (
            <Button startIcon={<TransformIcon />} onClick={handleConverter} disabled={busy}>
              Converter em pedido
            </Button>
          )}
          {detail?.tipo === 'Pedido' && detail?.status === 'Aberto' && (
            <>
              <Button
                startIcon={<ReceiptLongIcon />}
                onClick={handleFaturar}
                disabled={busy}
                variant="outlined"
              >
                Faturar
              </Button>
              <Button onClick={handleNfe} disabled={busy} variant="contained">
                NF-e
              </Button>
            </>
          )}
          {(detail?.status === 'Aberto' || detail?.status === 'EmCaixa') && (
            <Button
              color="error"
              startIcon={<CancelOutlinedIcon />}
              onClick={handleCancel}
              disabled={busy}
            >
              Cancelar
            </Button>
          )}
          <Button onClick={() => setDetailOpen(false)}>Fechar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
